import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setMessage } from "../slices/message";
import { BASE_URL } from "./common";
import { setLogger } from "../slices/logger";

export const applicationsApi = createApi({
  reducerPath: "applicationsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      // By default, if we have a token in the store, let's use that for authenticated requests
      const token = getState().auth.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getAllApplications: builder.query({
      query: (filter) => {
        return {
          url: `/api/applications`,
          method: "GET",
          params: filter,
        };
      },
      transformResponse: (response, meta, arg) => {
        return response.data.map((app, index) => ({
          row: index + 1,
          ...app,
        }));
      },
      // the first argument below must have the same name as the first argument of `query` above.
      // if `query` takes no argument, the first argument below must be `undefined`
      async onQueryStarted(filter, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // TODO update the applications store?
          dispatch(
            setLogger(`Successfully retrieved ${data.length} applications!`)
          );
        } catch (err) {
          const msg =
            (err && err.error && err.error.data && err.error.data.message) ||
            "An error has occurred when reading your application data.";
          dispatch(
            setMessage({ text: msg, error: JSON.stringify(err, null, 2) })
          );
        }
      },
    }),
    submitApplication: builder.mutation({
      query: (application) => ({
        url: `/api/applications`,
        method: "POST",
        body: application,
      }),
      transformResponse: (response, meta, arg) => {
        return response.data;
      },
      async onQueryStarted(application, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // TODO store data?
          const msg =
            "Successfully submitted your application.<br/>" +
            "If you need to make any updates (or withdraw your application), please contact Prof. Selinski at" +
            " <a href='/mailto:joanne@cs.jhu.edu'><code>joanne@cs.jhu.edu</code></a>.";
          dispatch(setMessage({ text: msg }));
        } catch (err) {
          const msg =
            (err && err.error && err.error.data && err.error.data.message) ||
            "An error has occurred when submitting your application.";
          dispatch(
            setMessage({ text: msg, error: JSON.stringify(err, null, 2) })
          );
        }
      },
    }),
    updateApplication: builder.mutation({
      query: ({ _id, ...rest }) => ({
        url: `/api/applications/${_id}`,
        method: "PUT",
        body: rest,
      }),
      transformResponse: (response, meta, arg) => {
        return response.data;
      },
      async onQueryStarted(application, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // TODO store data?
          dispatch(
            setLogger(
              `Successfully updated application ${data._id} (${data.firstName} ${data.lastName})`
            )
          );
        } catch (err) {
          const msg =
            (err && err.error && err.error.data && err.error.data.message) ||
            "An error has occurred when updating the application.";
          dispatch(
            setMessage({ text: msg, error: JSON.stringify(err, null, 2) })
          );
        }
      },
    }),
  }),
});

export const {
  useGetAllApplicationsQuery,
  useLazyGetAllApplicationsQuery,
  useSubmitApplicationMutation,
  useUpdateApplicationMutation,
} = applicationsApi;
